/**
 * Pixel Tracking Module
 * Handles initialization and event tracking for Meta/Facebook, TikTok, Google Ads, and YouTube pixels
 */

// Type definitions for pixel objects
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    ttq?: {
      page: () => void;
      track: (event: string, data?: Record<string, any>) => void;
    };
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Initialize all pixel tracking scripts
 * Call this once when the app loads
 */
export function initPixels(): void {
  console.log('[Pixels] Initializing pixel tracking...');
  
  // Meta Pixel initialization is handled in index.html
  if (typeof window.fbq === 'function') {
    console.log('[Pixels] Meta Pixel initialized');
  } else {
    console.warn('[Pixels] Meta Pixel not loaded');
  }

  // TikTok Pixel initialization is handled in index.html
  if (window.ttq) {
    console.log('[Pixels] TikTok Pixel initialized');
  } else {
    console.warn('[Pixels] TikTok Pixel not loaded');
  }

  // Google Ads gtag initialization is handled in index.html
  if (typeof window.gtag === 'function') {
    console.log('[Pixels] Google Ads gtag initialized');
  } else {
    console.warn('[Pixels] Google Ads gtag not loaded');
  }
}

/**
 * Track a page view event across all pixels
 */
export function trackPageView(): void {
  console.log('[Pixels] Tracking page view...');

  // Meta Pixel - PageView is already tracked in index.html, but we can track it again if needed
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
    console.log('[Pixels] Meta Pixel: PageView tracked');
  }

  // TikTok Pixel - PageView is already tracked in index.html
  if (window.ttq) {
    window.ttq.page();
    console.log('[Pixels] TikTok Pixel: PageView tracked');
  }

  // Google Ads - PageView is automatically tracked via gtag config
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      send_to: 'YOUR_GOOGLE_ADS_ID'
    });
    console.log('[Pixels] Google Ads: PageView tracked');
  }

  // YouTube tracking via Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'YOUR_CONVERSION_LABEL',
      'event_category': 'engagement',
      'event_label': 'page_view'
    });
    console.log('[Pixels] YouTube (via Google Ads): PageView tracked');
  }
}

/**
 * Track a generic event across all pixels
 * @param eventName - Name of the event to track
 * @param params - Optional parameters to send with the event
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  console.log(`[Pixels] Tracking event: ${eventName}`, params);

  // Meta Pixel
  if (typeof window.fbq === 'function') {
    window.fbq('track', eventName, params || {});
    console.log(`[Pixels] Meta Pixel: ${eventName} tracked`);
  }

  // TikTok Pixel
  if (window.ttq) {
    window.ttq.track(eventName, params || {});
    console.log(`[Pixels] TikTok Pixel: ${eventName} tracked`);
  }

  // Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      ...params,
      send_to: 'YOUR_GOOGLE_ADS_ID'
    });
    console.log(`[Pixels] Google Ads: ${eventName} tracked`);
  }

  // YouTube tracking via Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'YOUR_CONVERSION_LABEL',
      'event_category': 'engagement',
      'event_label': eventName,
      ...params
    });
    console.log(`[Pixels] YouTube (via Google Ads): ${eventName} tracked`);
  }
}

/**
 * Track a CTA (Call-to-Action) button click
 * This is a specialized event for button clicks
 */
export function trackCTA(ctaName?: string): void {
  const params = {
    cta_name: ctaName || 'default'
  };

  console.log(`[Pixels] Tracking CTA click: ${ctaName || 'default'}`);

  // Meta Pixel
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', params);
    console.log('[Pixels] Meta Pixel: CTA click tracked as Lead event');
  }

  // TikTok Pixel
  if (window.ttq) {
    window.ttq.track('ClickButton', params);
    console.log('[Pixels] TikTok Pixel: CTA click tracked');
  }

  // Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'YOUR_GOOGLE_ADS_ID',
      'event_category': 'engagement',
      'event_label': 'cta_click',
      'value': 1,
      'currency': 'USD',
      ...params
    });
    console.log('[Pixels] Google Ads: CTA click tracked');
  }

  // YouTube tracking via Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'YOUR_CONVERSION_LABEL',
      'event_category': 'engagement',
      'event_label': 'cta_click',
      'value': 1,
      'currency': 'USD',
      ...params
    });
    console.log('[Pixels] YouTube (via Google Ads): CTA click tracked');
  }
}

/**
 * Track QR scan success
 */
export function trackQRScanSuccess(data: Record<string, any>): void {
  console.log('[Pixels] Tracking QR Scan Success', data);
  trackEvent('qr_scan_success', data);
}

/**
 * Track QR scan failure
 */
export function trackQRScanFailure(error: string): void {
  console.log('[Pixels] Tracking QR Scan Failure', error);
  trackEvent('qr_scan_failure', { error });
}

